import { FaAnchor } from "react-icons/fa";
import { imageWithCaption } from "../objects";
import { defineType } from "sanity";
import { multilingualStringPreview } from "../base/multilingualArrayString";
import { multilingualOrderingField } from "../../common/ordering";

export default defineType({
  name: "port",
  title: "Port",
  type: "document",
  icon: FaAnchor,
  orderings: [
    {
      title: "Name",
      name: "nameAsc",
      by: [{ field: multilingualOrderingField("name"), direction: "asc" }]
    }
  ],
  preview: {
    select: {
      name: "data.name",
      code: "data.code",
      region: "region.name",
      country: "data.country.name",
      media: "card.image"
    },
    prepare(value) {
      const name = multilingualStringPreview(value.name);
      const region = multilingualStringPreview(value.region);
      const country = multilingualStringPreview(value.country);
      return {
        title: value.code ? `${name} (${value.code})` : name,
        subtitle: region || country,
        media: value.media
      };
    }
  },
  fieldsets: [
    {
      name: "hero",
      title: "Hero",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "visaIncluded",
      title: "Visa Included",
      options: {
        collapsible: true,
        collapsed: true
      }
    }
  ],
  groups: [
    { name: "asCard", title: "Render as Card" },
    {
      name: "seo",
      title: "SEO, Routing & Tracking"
    }
  ],
  fields: [
    {
      name: "data",
      title: "Data",
      type: "reference",
      to: [{ type: "portData" }],
      readOnly: true
    },
    {
      name: "name",
      title: "Name",
      type: "multilingualArrayString"
    },
    {
      name: "code",
      title: "Code",
      type: "string",
      readOnly: true
    },
    {
      name: "region",
      title: "Region",
      description:
        "Only required when we need to be more specific than country (e.g. Tierra del Fuego)",
      type: "reference",
      to: [{ type: "region" }]
    },
    {
      name: "title",
      title: "Title",
      type: "multilingualArrayString",
      fieldset: "hero"
    },
    {
      name: "heroImages",
      title: "Images",
      type: "array",
      of: [{ type: "heroSlideImage" }],
      options: { hotspot: true },
      fieldset: "hero"
    },
    {
      name: "description",
      title: "Description",
      type: "descriptionText",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "shortDescriptionOverride",
      title: "Short Description (override)",
      description:
        "Used in itinerary etc. If not set, will use (English only) short description from Shop API.",
      type: "multilingualArrayBlockContent",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "slideshow",
      title: "Slideshow",
      type: "array",
      of: [
        {
          type: imageWithCaption.name
        }
      ]
    },
    {
      name: "hotelsContent",
      title: "Hotels",
      description: "leave empty to hide the section",
      type: "array",
      of: [{ type: "accordionContent" }],
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "card",
      title: "Render as Card",
      type: "asCard",
      group: "asCard"
    },
    {
      name: "metadata",
      title: "SEO, Routing & Tracking",
      type: "metadata",
      group: "seo"
    },
    {
      name: "visaIncludedEnabled",
      title: "Enable Visa Label",
      type: "boolean",
      fieldset: "visaIncluded",
      description:
        "Show 'VISA INCLUDED' label for this port in day-by-day itinerary",
      initialValue: false
    },
    {
      name: "visaLabelTitle",
      title: "Visa Label Title",
      type: "multilingualArrayString",
      fieldset: "visaIncluded",
      description: "Text for the label",
      hidden: ({ parent }) => !parent?.visaIncludedEnabled
    },
    {
      name: "visaInfoText",
      title: "Visa Included Description",
      type: "multilingualArrayText",
      fieldset: "visaIncluded",
      description:
        "Detailed information displayed in the expanded port description",
      hidden: ({ parent }) => !parent?.visaIncludedEnabled
    },
    {
      name: "visaIncludedCruises",
      title: "Cruises with Visa Included",
      type: "array",
      fieldset: "visaIncluded",
      of: [{ type: "string" }],
      description: "List of cruise codes for which the label should be shown",
      hidden: ({ parent }) => !parent?.visaIncludedEnabled
    }
  ]
});
